// Report & spam protection (like WhatsApp's): report a user or group, with the
// last 5 messages if you choose (chats are end-to-end encrypted: only you can
// include them), and block / leave too. Chats from someone you haven't saved
// start with a "Not a contact" bar: Block · Report · Add.
import { useState } from "preact/hooks";
import { api } from "../api";
import * as S from "../state";
import { phoneOf } from "../util";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";

const REASONS: [string, string][] = [
  ["spam", "Spam or unwanted messages"],
  ["scam", "Scam or fraud"],
  ["harassment", "Harassment or bullying"],
  ["inappropriate", "Inappropriate content"],
  ["other", "Something else"],
];

export function ReportDialog({ jid, onClose }: { jid: string; onClose: () => void }) {
  const group = S.isGroup(jid);
  const [reason, setReason] = useState("spam");
  const [include, setInclude] = useState(true);
  const [also, setAlso] = useState(true); // block (person) / exit (group)
  const [busy, setBusy] = useState(false);
  const name = S.displayName(jid);
  async function send() {
    setBusy(true);
    try {
      const e = engine.value!;
      const messages = include
        ? (await e.allMessages(jid))
            .filter((m) => m.dir === "in" && !m.deleted && !m.undecryptable && m.kind !== "system" && m.kind !== "call")
            .slice(-5)
            .map((m) => ({ from: m.from, ts: m.ts, text: m.body || m.kind }))
        : undefined;
      await api.report({ jid, reason, messages });
      if (also && !group) await e.setBlocked(jid, true);
      if (also && group) await api.removeGroupMember(jid, e.me).then(() => e.loadGroups());
      toast(also ? (group ? `Reported ${name} and left the group` : `Reported and blocked ${name}`) : `Reported ${name}`);
      onClose();
    } catch (err) {
      toast((err as Error).message);
      setBusy(false);
    }
  }
  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal report-dialog" role="dialog" aria-label={`Report ${name}`} onClick={(e) => e.stopPropagation()}>
        <h2>Report {name}?</h2>
        <p class="muted small">{group ? "Admins and members won't be told." : `${name} won't be told.`} Pick what's wrong:</p>
        {REASONS.map(([v, label]) => (
          <label key={v} class="radio-row">
            <input type="radio" name="report-reason" checked={reason === v} onChange={() => setReason(v)} aria-label={label} />
            {label}
          </label>
        ))}
        <label class="toggle">
          <span>
            <strong>Include the last 5 messages</strong>
            <small class="muted">They're end-to-end encrypted: only you can send them with the report.</small>
          </span>
          <input type="checkbox" checked={include} onChange={(e) => setInclude(e.currentTarget.checked)} aria-label="Include the last 5 messages" />
        </label>
        <label class="toggle">
          <span>
            <strong>{group ? "Exit the group" : `Block ${name}`}</strong>
          </span>
          <input type="checkbox" checked={also} onChange={(e) => setAlso(e.currentTarget.checked)} aria-label={group ? "Exit the group" : "Block too"} />
        </label>
        <div class="row-buttons">
          <button class="link" onClick={onClose}>
            Cancel
          </button>
          <button class="primary danger-btn" disabled={busy} onClick={() => void send()}>
            Report
          </button>
        </div>
      </div>
    </div>
  );
}

/** "Not a contact" (1:1 chats from someone you haven't saved, until you reply). */
export function UnknownSenderBar({ jid }: { jid: string }) {
  const [report, setReport] = useState(false);
  const [hidden, setHidden] = useState(false);
  const r = S.roster.value.get(jid);
  const saved = r && (r.subscription === "to" || r.subscription === "both");
  const replied = S.messages.value.some((m) => m.chat === jid && m.dir === "out" && m.kind !== "system");
  const theirs = S.messages.value.some((m) => m.chat === jid && m.dir === "in" && m.kind !== "system");
  if (saved || replied || !theirs || hidden || S.isGroup(jid) || S.isChannel(jid) || S.isBroadcast(jid) || S.blocked.value.has(jid)) return report ? <ReportDialog jid={jid} onClose={() => setReport(false)} /> : null;
  const add = () =>
    void api.addContact(phoneOf(jid), S.profiles.value.get(jid)?.name ?? "").then(
      () => (toast("Contact saved"), setHidden(true)),
      (e) => toast((e as Error).message),
    );
  return (
    <div class="unknown-bar" role="region" aria-label="Not a contact">
      <span class="unknown-icon">
        <Icon name="info" size={18} />
      </span>
      <span class="grow small">
        <strong>{phoneOf(jid)}</strong> isn't in your contacts.
      </span>
      <button class="chip danger-chip" onClick={() => void engine.value?.setBlocked(jid, true).then(() => toast("Blocked"))}>
        Block
      </button>
      <button class="chip" onClick={() => setReport(true)}>
        Report
      </button>
      <button class="chip on" onClick={add}>
        Add
      </button>
      {report && <ReportDialog jid={jid} onClose={() => setReport(false)} />}
    </div>
  );
}

/** "Report …" in contact / group info. */
export function ReportButton({ jid }: { jid: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button class="list-action danger" onClick={() => setOpen(true)}>
        <span class="list-action-icon">
          <Icon name="block" />
        </span>
        Report {S.isGroup(jid) ? "group" : S.displayName(jid)}
      </button>
      {open && <ReportDialog jid={jid} onClose={() => setOpen(false)} />}
    </>
  );
}
